/**
 * The iManage area's pages outside a workspace: the workspaces of a library
 * (the area's home), Search, Recent, Favorites and Saved by Amoreg. Lists that
 * hold documents open them in the same preview pane as a workspace does.
 *
 * @module components/apps/imanage/IManagePages
 */
import type { IManageDocument, IManageEntry } from "@amoreg/contracts/imanage";
import { Drawer } from "@lucasmarkes/hairline/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { BriefcaseIcon, CloudUploadIcon, SearchIcon, SearchXIcon, TriangleAlertIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { useRowKeys } from "../../regulation/finding/FindingsQueue";
import { SegmentedTabs } from "../../regulation/PageHeader";
import { Skeleton } from "../../ui/skeleton";
import { useIManageArea } from "./imanageAreaContext";
import { IManageDocumentTable, type IManageSort } from "./IManageDocumentTable";
import { formatDateTime, formatWhen, sortEntries } from "./imanageFormat";
import { EmptyList, IManagePageHeader, IManageSearchField, IManageSplit } from "./IManageLayout";
import { entryFromRef, useIManageList } from "./imanageLocalLists";
import { documentKey, libraryOfId, patchSearch, sortFromSearch } from "./imanagePaths";
import { invalidateIManage, useIManageLibrary, useSearch, useWorkspaces } from "./imanageQueries";
import { InlineError, MatterNumber } from "./IManageParts";
import { IManagePreviewPane } from "./IManagePreviewPane";
import { IManageReviewPanel } from "./IManageReviewPanel";
import { useFavoriteRows, type IManageViewProps } from "./IManageWorkspaceView";

// ── Home: the library's workspaces ─────────────────────────────────────────

export function IManageHomePage({ search, onSearchChange }: IManageViewProps) {
  const { organizationId, orgSegment } = useIManageArea();
  const { library, libraries, choose } = useIManageLibrary(organizationId, true, search.lib ?? null);
  const workspaces = useWorkspaces(organizationId, library);
  const [filter, setFilter] = useState("");
  // A link that names a library makes it the chosen one (the sidebar follows).
  useEffect(() => {
    if (search.lib) choose(search.lib);
  }, [search.lib, choose]);
  const listRef = useRef<HTMLDivElement>(null);
  useRowKeys(listRef);
  const offered = libraries.data?.entries ?? [];
  const rows = useMemo(() => {
    const all = workspaces.data?.entries ?? [];
    const term = filter.trim().toLowerCase();
    return term
      ? all.filter((w) =>
          [w.name, w.client, w.matter, w.clientName, w.matterName, w.owner]
            .filter(Boolean)
            .some((value) => value!.toLowerCase().includes(term)),
        )
      : all;
  }, [workspaces.data, filter]);

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-imanage-home="">
      <IManagePageHeader
        title={
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-[15px] font-semibold">Workspaces</h1>
            {offered.length > 1 && library ? (
              <SegmentedTabs
                label="Library"
                value={library}
                onValueChange={(next) => {
                  choose(next);
                  onSearchChange(patchSearch(search, { lib: next }), { replace: true });
                }}
                tabs={offered.map((l) => ({ value: l.id, label: l.name }))}
              />
            ) : null}
          </div>
        }
        actions={
          <IManageSearchField
            className="w-48 sm:w-64"
            value={filter}
            onCommit={setFilter}
            placeholder="Filter workspaces"
          />
        }
      />
      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
        {libraries.error && !libraries.data ? (
          <InlineError className="m-5" message={libraries.error} onRetry={libraries.refresh} />
        ) : workspaces.error && !workspaces.data ? (
          <InlineError className="m-5" message={workspaces.error} onRetry={workspaces.refresh} />
        ) : (
          <WorkspaceTable
            orgSegment={orgSegment}
            rows={rows}
            loading={libraries.loading || workspaces.loading || (!library && !libraries.data)}
            truncated={workspaces.data?.nextOffset != null}
            empty={
              filter ? (
                <EmptyList icon={<SearchXIcon />} title={`No workspace matches “${filter}”`} />
              ) : (
                <EmptyList
                  art={Drawer}
                  title="No workspaces"
                  body="Your iManage account has no workspaces in this library."
                />
              )
            }
          />
        )}
      </div>
    </div>
  );
}

function WorkspaceTable({
  orgSegment,
  rows,
  loading,
  truncated,
  empty,
}: {
  readonly orgSegment: string;
  readonly rows: readonly IManageEntry[];
  readonly loading: boolean;
  readonly truncated: boolean;
  readonly empty: ReactNode;
}) {
  const head = (
    <div className="sticky top-0 z-10 flex h-8 items-center gap-3 border-b border-border/60 bg-background px-5 text-xs text-muted-foreground">
      <span className="w-20 shrink-0">Number</span>
      <span className="min-w-0 flex-1">Workspace</span>
      <span className="hidden w-56 shrink-0 @2xl:block">Client</span>
      <span className="hidden w-36 shrink-0 @xl:block">Responsible</span>
      <span className="w-24 shrink-0">Last activity</span>
    </div>
  );
  if (loading)
    return (
      <div aria-busy="true" aria-label="Loading workspaces" className="@container">
        {head}
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="flex h-10 items-center gap-3 border-b border-border/40 px-5">
            <Skeleton className="h-3.5 w-14" />
            <Skeleton className="h-3.5" style={{ width: `${32 - ((i * 7) % 14)}%` }} />
            <Skeleton className="ms-auto h-3.5 w-16" />
          </div>
        ))}
      </div>
    );
  return (
    <div className="@container" role="list" aria-label="Workspaces">
      {head}
      {rows.map((w) => (
        <Link
          key={w.id}
          to="/$org/apps/imanage/w/$workspaceId"
          params={{ org: orgSegment, workspaceId: w.id }}
          data-row-link=""
          role="listitem"
          className="flex h-10 items-center gap-3 border-b border-border/40 px-5 text-[13px] outline-none transition-colors hover:bg-accent/50 focus-visible:bg-accent/70 focus-visible:shadow-[inset_2px_0_0_var(--color-ring)]"
        >
          <MatterNumber entry={w} className="w-20 text-xs" />
          <span className="flex min-w-0 flex-1 items-center gap-2.5">
            <BriefcaseIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate font-medium">{w.name}</span>
            {w.matterName ? (
              <span className="hidden min-w-0 truncate text-muted-foreground @3xl:inline">{w.matterName}</span>
            ) : null}
          </span>
          <span className="hidden w-56 shrink-0 truncate text-muted-foreground @2xl:block">{w.clientName ?? ""}</span>
          <span className="hidden w-36 shrink-0 truncate text-muted-foreground @xl:block">{w.owner ?? ""}</span>
          <span className="w-24 shrink-0 text-muted-foreground tabular-nums" title={formatDateTime(w.modified)}>
            {formatWhen(w.modified)}
          </span>
        </Link>
      ))}
      {rows.length === 0 ? empty : null}
      {truncated ? (
        <p className="px-5 py-2 text-xs text-muted-foreground">Showing the first 50 workspaces. Filter to narrow them.</p>
      ) : null}
    </div>
  );
}

// ── Lists of documents across workspaces ───────────────────────────────────

function DocumentListPage({
  title,
  meta,
  tools,
  rows,
  loading,
  error,
  onRetry,
  empty,
  search,
  onSearchChange,
  notice,
}: IManageViewProps & {
  readonly title: ReactNode;
  readonly meta?: ReactNode;
  readonly tools?: ReactNode;
  readonly rows: readonly IManageEntry[];
  readonly loading: boolean;
  readonly error?: string | null;
  readonly onRetry?: () => void;
  readonly empty: ReactNode;
  readonly notice?: ReactNode;
}) {
  const { organizationId, orgSegment, status } = useIManageArea();
  const { library } = useIManageLibrary(organizationId, true, search.lib ?? null);
  const navigate = useNavigate();
  const favorites = useFavoriteRows(organizationId, library);
  const workspaces = useWorkspaces(organizationId, library);
  const locate = useMemo(() => {
    const names = new Map(
      (workspaces.data?.entries ?? []).map(
        (w): [string, string] => [w.id, [w.client, w.name].filter(Boolean).join(" ")],
      ),
    );
    return (entry: IManageEntry) =>
      entry.kind === "matter"
        ? (entry.clientName ?? null)
        : entry.workspaceId
          ? (names.get(entry.workspaceId) ?? null)
          : null;
  }, [workspaces.data]);
  const [review, setReview] = useState<IManageDocument | null>(null);
  const sort: IManageSort = sortFromSearch(search);
  const docLibrary = (search.doc && libraryOfId(search.doc)) || library || "";

  const open = (entry: IManageEntry) => {
    if (entry.kind === "matter")
      void navigate({
        to: "/$org/apps/imanage/w/$workspaceId",
        params: { org: orgSegment, workspaceId: entry.id },
      });
    else if (entry.kind === "folder" && entry.workspaceId)
      void navigate({
        to: "/$org/apps/imanage/w/$workspaceId/$folderId",
        params: { org: orgSegment, workspaceId: entry.workspaceId, folderId: entry.id },
      });
    else if (entry.kind === "document") onSearchChange(patchSearch(search, { doc: entry.id, v: null }));
  };
  const header = (
    <IManagePageHeader
      title={
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-[15px] font-semibold">{title}</h1>
          {meta ? <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{meta}</span> : null}
        </div>
      }
      actions={tools}
    />
  );
  if (review && review.workspaceId)
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {header}
        <IManageReviewPanel
          organizationId={organizationId}
          library={docLibrary}
          workspaceId={review.workspaceId}
          document={review}
          folder={null}
          canUpload={status.canUpload}
          canSaveVersion={status.canSaveVersion}
          onClose={() => setReview(null)}
          onSaved={() => invalidateIManage(organizationId, ["browse", "search", "versions", "document", "status"])}
        />
      </div>
    );
  const preview = search.doc ? (
    <IManagePreviewPane
      key={documentKey(search.doc)}
      organizationId={organizationId}
      orgSegment={orgSegment}
      library={docLibrary}
      documentId={search.doc}
      versionId={search.v ?? null}
      place={{}}
      canUpload={status.canUpload}
      canSaveVersion={status.canSaveVersion}
      canDownload={status.operations.includes("download")}
      onClose={() => onSearchChange(patchSearch(search, { doc: null, v: null }))}
      onSelectVersion={(v) => onSearchChange(patchSearch(search, { v }), { replace: true })}
      onReview={setReview}
      // The review panel saves into the document's workspace; a list row may not know it.
      canReview={(document) => Boolean(document.workspaceId)}
      onSaved={() => invalidateIManage(organizationId, ["browse", "search", "versions", "document", "status"])}
    />
  ) : null;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {header}
      <IManageSplit preview={preview} onClosePreview={() => onSearchChange(patchSearch(search, { doc: null, v: null }))}>
        {notice}
        {error && rows.length === 0 ? (
          <InlineError className="m-5" message={error} {...(onRetry ? { onRetry } : {})} />
        ) : (
          <IManageDocumentTable
            label={typeof title === "string" ? title : "Documents"}
            groups={[{ key: "rows", rows: sortEntries(rows, sort.key, sort.dir) }]}
            sort={sort}
            onSortChange={(next) => onSearchChange(patchSearch(search, { sort: next.key, dir: next.dir }), { replace: true })}
            selectedId={search.doc ?? null}
            onOpen={open}
            loading={loading}
            isFavorite={favorites.isFavorite}
            onToggleFavorite={favorites.toggle}
            locate={locate}
            empty={empty}
          />
        )}
      </IManageSplit>
    </div>
  );
}

export function IManageSearchPage(props: IManageViewProps) {
  const { search, onSearchChange } = props;
  const { organizationId } = useIManageArea();
  const { library, libraries, choose } = useIManageLibrary(organizationId, true, search.lib ?? null);
  const query = search.q ?? "";
  const results = useSearch(organizationId, library, query, null);
  const offered = libraries.data?.entries ?? [];
  const rows = results.data?.entries ?? [];
  return (
    <DocumentListPage
      {...props}
      title="Search"
      meta={query && results.data ? `${rows.length}${results.data.nextOffset !== null ? "+" : ""} results` : undefined}
      tools={
        <>
          {offered.length > 1 && library ? (
            <SegmentedTabs
              label="Library"
              value={library}
              onValueChange={(next) => {
                choose(next);
                onSearchChange(patchSearch(search, { lib: next }), { replace: true });
              }}
              tabs={offered.map((l) => ({ value: l.id, label: l.name }))}
            />
          ) : null}
          <IManageSearchField
            autoFocus={!query}
            className="w-56 sm:w-80"
            value={query}
            placeholder={`Search ${offered.find((l) => l.id === library)?.name ?? "iManage"}`}
            onCommit={(q) => onSearchChange(patchSearch(search, { q, doc: null, v: null }))}
          />
        </>
      }
      rows={rows}
      loading={query.length > 0 && results.loading}
      error={results.error}
      onRetry={results.refresh}
      empty={
        query ? (
          <EmptyList
            icon={<SearchXIcon />}
            title={`Nothing matches “${query}”`}
            body="Search covers document names and full text, with your own iManage permissions."
          />
        ) : (
          <EmptyList
            icon={<SearchIcon />}
            title="Search iManage"
            body="Find documents, precedents and filed emails across the library by name or by what they say."
          />
        )
      }
    />
  );
}

export function IManageRecentPage(props: IManageViewProps) {
  const { organizationId } = useIManageArea();
  const [recent] = useIManageList(organizationId, "recent");
  return (
    <DocumentListPage
      {...props}
      title="Recent"
      meta={recent.length || undefined}
      rows={recent.map(entryFromRef)}
      loading={false}
      empty={
        <EmptyList
          art={Drawer}
          title="Nothing opened yet"
          body="Documents you open in Amoreg appear here, newest first. The list stays in this browser."
        />
      }
    />
  );
}

export function IManageFavoritesPage(props: IManageViewProps) {
  const { organizationId } = useIManageArea();
  const [favorites] = useIManageList(organizationId, "favorites");
  return (
    <DocumentListPage
      {...props}
      title="Favorites"
      meta={favorites.length || undefined}
      rows={favorites.map(entryFromRef)}
      loading={false}
      empty={
        <EmptyList
          art={Drawer}
          title="No favorites yet"
          body="Star a workspace, folder or document to keep it here. Favorites stay in this browser."
        />
      }
    />
  );
}

export function IManageSavedPage(props: IManageViewProps) {
  const { status } = useIManageArea();
  const saved = status.saves
    .filter((save) => save.status === "saved" && save.receipt)
    .sort((a, b) => (b.savedAt ?? 0) - (a.savedAt ?? 0))
    .map((save) => {
      const receipt = save.receipt!;
      return save.savedAt !== undefined && !receipt.modified
        ? { ...receipt, modified: new Date(save.savedAt).toISOString() }
        : receipt;
    });
  const unknown = status.saves.filter((save) => save.status === "saving" || save.status === "uncertain");
  return (
    <DocumentListPage
      {...props}
      title="Saved by Amoreg"
      meta={saved.length || undefined}
      rows={saved}
      loading={false}
      notice={
        unknown.length ? (
          <div
            role="status"
            className={cn(
              "mx-5 mt-4 flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2.5 text-[13px]",
            )}
          >
            <TriangleAlertIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-warning" />
            <span>
              {unknown.length === 1 ? "One save has" : `${unknown.length} saves have`} an unknown outcome. Check
              iManage before saving that document again. Reference{unknown.length === 1 ? "" : "s"}:{" "}
              <span className="font-mono text-xs">{unknown.map((s) => s.id).join(", ")}</span>
            </span>
          </div>
        ) : null
      }
      empty={
        <EmptyList
          icon={<CloudUploadIcon />}
          title="Nothing saved from Amoreg yet"
          body="Documents you review here and save back to iManage, after confirming, are listed with their receipts."
        />
      }
    />
  );
}
